/**
 * IANA timezone helpers — the single source for zone detection, enumeration and
 * labelling. Two copies of this existed before: the orphaned
 * `components/home/time-widget.tsx` and the live automation trigger picker.
 *
 * Everything here goes through the platform `Intl` API. No date library is
 * needed for zone conversion, and `moment-timezone` is legacy.
 */

/** Used when the engine cannot enumerate zones (older Safari). */
const FALLBACK_ZONES = [
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
];

/**
 * The zone this device is in. On the server this is the SERVER's zone, which is
 * not what any user wants — call it from the client only, or let the caller
 * treat `undefined` as "use the runtime default".
 */
export function getBrowserTimeZone(): string {
  try {
    // Canonicalised: engines report Asia/Calcutta for what users call Kolkata.
    return canonicalZone(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  } catch {
    return "UTC";
  }
}

/**
 * Legacy IANA names the engine still enumerates, mapped to the current ones.
 *
 * `Intl.supportedValuesOf("timeZone")` returns the OLD aliases — Asia/Calcutta,
 * Europe/Kiev, Asia/Katmandu — even though `Intl.DateTimeFormat` accepts the
 * modern spellings. Listing the engine's output verbatim means someone
 * searching "Kolkata" finds nothing, and it puts a politically superseded
 * spelling of Kyiv in front of users. We show the modern name and keep the old
 * one searchable.
 */
const RENAMED_ZONES: Record<string, string> = {
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Europe/Kiev": "Europe/Kyiv",
  "Europe/Uzhgorod": "Europe/Kyiv",
  "Europe/Zaporozhye": "Europe/Kyiv",
  "America/Godthab": "America/Nuuk",
  "Pacific/Enderbury": "Pacific/Kanton",
};

/** Modern name → the legacy spelling, so search still matches what users type. */
const LEGACY_SPELLINGS: Record<string, string[]> = Object.entries(RENAMED_ZONES).reduce(
  (acc, [legacy, modern]) => {
    const existing = acc[modern];
    if (existing) existing.push(legacy);
    else acc[modern] = [legacy];
    return acc;
  },
  {} as Record<string, string[]>,
);

/** The current name for a zone, whichever spelling came in. */
export function canonicalZone(zone: string): string {
  return RENAMED_ZONES[zone] ?? zone;
}

/** Every zone the engine knows, under its current name. Falls back to a short list. */
export function listTimeZones(): string[] {
  try {
    const supported = (Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.(
      "timeZone",
    );
    if (supported?.length) {
      const canonical = supported.map(canonicalZone).filter(isValidTimeZone);
      return [...new Set(canonical)].sort();
    }
  } catch {
    // fall through
  }
  return FALLBACK_ZONES;
}

/** True when the engine accepts this as an IANA zone. */
export function isValidTimeZone(zone: string): boolean {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** "Asia/Kolkata" → "Kolkata", for a picker row. */
export function zoneCityLabel(zone: string): string {
  const city = zone.split("/").pop() ?? zone;
  return city.replace(/_/g, " ");
}

/** "Asia/Kolkata" → "Asia · Kolkata", for a full-width label. */
export function zoneFullLabel(zone: string): string {
  return zone.replace(/_/g, " ").replace(/\//g, " · ");
}

/**
 * The zone's UTC offset right now, e.g. "GMT+5:30". Computed rather than
 * hardcoded because it moves with daylight saving.
 */
export function zoneOffsetLabel(zone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "shortOffset",
      hour: "numeric",
    }).formatToParts(at);
    return parts.find((part) => part.type === "timeZoneName")?.value ?? zone;
  } catch {
    return zone;
  }
}

/** "(GMT+5:30) Kolkata" — how the settings row and picker name a zone. */
export function zoneDisplayLabel(zone: string, at: Date = new Date()): string {
  return `(${zoneOffsetLabel(zone, at)}) ${zoneCityLabel(zone)}`;
}

/**
 * The viewer's display zone, readable from code that cannot use a hook —
 * plain modules like home/utils.ts, IndexedDB eviction, meeting titles.
 *
 * SSR SAFETY: never set on the server. Module state there is shared across
 * every request, so one user's zone would leak into another's render. On the
 * server this stays undefined and formatters fall back to the runtime default,
 * which is exactly the behaviour before the preference existed.
 */
let ambientTimeZone: string | undefined;

export function setAmbientTimeZone(zone: string | undefined): void {
  if (typeof window === "undefined") return;
  ambientTimeZone = zone;
}

export function getAmbientTimeZone(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return ambientTimeZone;
}

/**
 * Renders a stored INSTANT (a comment's createdAt, an activity timestamp) in
 * the viewer's zone.
 *
 * Only for values that are a real moment in time. A date property cell is a
 * wall clock, not an instant — converting one shifts a birthday or a due date
 * by a day for anyone west of the author. Those go through lib/date-value.
 *
 * `timeZone: undefined` means "use the runtime default", which is what every
 * call site did before the preference existed — so it is the correct value
 * during SSR and before the preference loads.
 */
const DEFAULT_INSTANT_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  year: "numeric",
  month: "short",
  day: "numeric",
};

export function formatInstant(
  value: string | number | Date | null | undefined,
  /** How to render it. Omit for a date-and-time default. */
  options: Intl.DateTimeFormatOptions = DEFAULT_INSTANT_FORMAT,
  /** Override the viewer's ambient zone. Only tests and fixed-zone views need this. */
  zone?: string,
): string {
  if (value === null || value === undefined || value === "") return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const timeZone = zone ?? options.timeZone ?? getAmbientTimeZone();
  try {
    return date.toLocaleString([], { ...options, timeZone });
  } catch {
    // An invalid stored zone must not blank out a timestamp.
    return date.toLocaleString([], { ...options, timeZone: undefined });
  }
}

/**
 * A Date whose LOCAL calendar parts read as the current wall clock in `zone`.
 *
 * This is the seam that lets zone-unaware code become zone-aware without
 * learning about zones. Everything downstream — moment's `startOf("day")`, the
 * day-string formatters, the relative filter resolvers — reads LOCAL parts, so
 * handing them a Date already shifted to the user's zone gives the right
 * calendar day with no other change.
 *
 * The returned Date is NOT the same instant as `at`; it is deliberately offset
 * so its local parts match the target zone. Use it only to answer "what day and
 * time is it there" — never to store or compare as an instant.
 *
 * `zone` undefined means "this device", which is what every caller did before
 * the preference existed.
 */
export function zonedNow(zone: string | undefined, at: Date = new Date()): Date {
  if (!zone) return at;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(at);
    const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
    const year = read("year");
    const month = read("month");
    const day = read("day");
    if (!year || !month || !day) return at;
    // hour can legitimately be 0; `hourCycle: h23` is not universally honoured,
    // so a formatted "24" is normalised here.
    const hour = read("hour") % 24;
    return new Date(year, month - 1, day, Number.isNaN(hour) ? 0 : hour, read("minute") || 0, read("second") || 0, 0);
  } catch {
    return at;
  }
}

/** Local-frame Date → the same wall clock read as an instant in `zone`. */
function wallClockToInstant(wall: Date, zone: string): Date {
  // Solve for the offset: guess that the wall clock is UTC, see what clock that
  // instant actually shows in `zone`, and correct by the difference. One more
  // pass settles DST boundaries, where the first correction lands on the other
  // side of the transition and the offset changes under it.
  const target = Date.UTC(
    wall.getFullYear(),
    wall.getMonth(),
    wall.getDate(),
    wall.getHours(),
    wall.getMinutes(),
    wall.getSeconds(),
  );
  let instant = target;
  for (let pass = 0; pass < 2; pass++) {
    const shown = zonedNow(zone, new Date(instant));
    const shownUtc = Date.UTC(
      shown.getFullYear(),
      shown.getMonth(),
      shown.getDate(),
      shown.getHours(),
      shown.getMinutes(),
      shown.getSeconds(),
    );
    const drift = shownUtc - target;
    if (drift === 0) break;
    instant -= drift;
  }
  return new Date(instant);
}

/**
 * Re-reads a wall clock written in `fromZone` as the wall clock someone in
 * `toZone` would see at the same moment.
 *
 * This is what a declared column zone means: the stored "09:30" was 09:30
 * *there*, so a viewer elsewhere sees the same instant on their own clock —
 * possibly on a different calendar day. Returns a local-frame Date, matching
 * how date cells are parsed.
 *
 * Either zone undefined, or the two being equal, returns the wall clock
 * untouched — which is today's behaviour and the no-column-zone default.
 *
 * ONLY for values that carry a time. A date-only cell is a floating calendar
 * day; converting one shifts a birthday or a due date for half the world.
 */
export function convertWallClock(wall: Date, fromZone: string | undefined, toZone: string | undefined): Date {
  if (!fromZone || !toZone || fromZone === toZone) return wall;
  if (!isValidTimeZone(fromZone) || !isValidTimeZone(toZone)) return wall;
  return zonedNow(toZone, wallClockToInstant(wall, fromZone));
}

/**
 * The instant a wall clock written in `zone` refers to. Used for sorting, where
 * two cells must be ordered by when they actually happen rather than by the
 * digits they display.
 */
export function wallClockInstant(wall: Date, zone: string | undefined): Date {
  if (!zone || !isValidTimeZone(zone)) return wall;
  return wallClockToInstant(wall, zone);
}

/**
 * Case-insensitive match on the city, the region and the offset, so "kolkata",
 * "asia" and "+5:30" all find Asia/Kolkata.
 */
export function matchesZoneQuery(zone: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const names = [zone, ...(LEGACY_SPELLINGS[zone] ?? [])];
  return (
    names.some((name) => name.toLowerCase().replace(/_/g, " ").includes(q)) ||
    zoneOffsetLabel(zone).toLowerCase().includes(q)
  );
}
